import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { termsApiService } from '../services/termsApiService';
import type {
  Terms,
  AnafTerms,
  TermsState,
  LoanAnafTermsResponse,
  PartnerTerms,
} from '../types/terms';

const initialTermsState: TermsState = {
  selectAll: false,
  notifications: false,
  bc: false,
  anaf: false,
};

const ONE_HOUR = 1000 * 60 * 60;

export const TermsQueryKeys = {
  acceptedTerms: 'acceptedTerms',
};

interface UseTermsReturn {
  getBcTerms: () => Promise<Terms | undefined>;
  bcTerms: Terms | undefined;
  acceptBcTerms: () => Promise<void>;

  getAnafTerms: () => Promise<AnafTerms | undefined>;
  anafTerms: AnafTerms | undefined;
  acceptAnafTerms: () => Promise<void>;

  getPartnerAnafTerms: () => Promise<LoanAnafTermsResponse | undefined>;
  partnerAnafTerms: LoanAnafTermsResponse | undefined;
  acceptPartnerAnafTerms: () => Promise<void>;

  getPartnerTerms: () => Promise<PartnerTerms | undefined>;
  partnerTerms: PartnerTerms | undefined;

  isLoading: boolean;

  // Terms state management
  acceptedTerms: TermsState;
  setAcceptedTerms: (terms: TermsState) => void;
  clearAcceptedTerms: () => void;
}

export const useTerms = (): UseTermsReturn => {
  const queryClient = useQueryClient();

  // Use React Query to store acceptedTerms state in cache
  const acceptedTermsQuery = useQuery<TermsState>({
    queryKey: [TermsQueryKeys.acceptedTerms],
    queryFn: () => initialTermsState,
    initialData: initialTermsState,
    staleTime: Infinity,
  });

  const acceptedTerms = acceptedTermsQuery.data ?? initialTermsState;

  const setAcceptedTerms = (terms: TermsState) => {
    queryClient.setQueryData<TermsState>(['acceptedTerms'], (oldData) => {
      const current = oldData ?? initialTermsState;
      return {
        ...current,
        ...terms,
      };
    });
  };

  const clearAcceptedTerms = () => {
    queryClient.setQueryData<TermsState>(
      [TermsQueryKeys.acceptedTerms],
      initialTermsState
    );
  };

  // BC Terms
  const bcTermsQuery = useQuery({
    queryKey: ['terms', 'bc'],
    queryFn: () => termsApiService.getBcTerms(),
    staleTime: ONE_HOUR,
  });

  const acceptBcTermsMutation = useMutation({
    mutationFn: () => termsApiService.acceptBcTerms(),
  });

  // ANAF Terms
  const anafTermsQuery = useQuery({
    queryKey: ['terms', 'anaf'],
    queryFn: () => termsApiService.getAnafTerms(),
  });

  const acceptAnafTermsMutation = useMutation({
    mutationFn: () => termsApiService.acceptAnafTerms(),
  });

  // Partner ANAF Terms
  const partnerAnafTermsQuery = useQuery({
    queryKey: ['terms', 'partner-anaf'],
    queryFn: () => termsApiService.getPartnerAnafTerms(),
  });

  const acceptPartnerAnafTermsMutation = useMutation({
    mutationFn: () => termsApiService.acceptPartnerAnafTerms(),
  });

  // Partner Terms (wlc/partner/terms)
  const partnerTermsQuery = useQuery({
    queryKey: ['terms', 'partner'],
    queryFn: () => termsApiService.getPartnerTerms(),
    staleTime: ONE_HOUR,
  });

  const getBcTerms = async () => {
    const result = await bcTermsQuery.refetch();
    return result.data;
  };

  const getAnafTerms = async () => {
    const result = await anafTermsQuery.refetch();
    return result.data;
  };

  const getPartnerAnafTerms = async () => {
    const result = await partnerAnafTermsQuery.refetch();
    return result.data;
  };

  const getPartnerTerms = async () => {
    const result = await partnerTermsQuery.refetch();
    return result.data;
  };

  const isLoading =
    bcTermsQuery.isLoading ||
    anafTermsQuery.isLoading ||
    partnerAnafTermsQuery.isLoading ||
    partnerTermsQuery.isLoading ||
    acceptBcTermsMutation.isPending ||
    acceptAnafTermsMutation.isPending ||
    acceptPartnerAnafTermsMutation.isPending;

  return {
    getBcTerms,
    bcTerms: bcTermsQuery.data,
    acceptBcTerms: () => acceptBcTermsMutation.mutateAsync(),

    getAnafTerms,
    anafTerms: anafTermsQuery.data,
    acceptAnafTerms: () => acceptAnafTermsMutation.mutateAsync(),

    getPartnerAnafTerms,
    partnerAnafTerms: partnerAnafTermsQuery.data,
    acceptPartnerAnafTerms: () => acceptPartnerAnafTermsMutation.mutateAsync(),

    getPartnerTerms,
    partnerTerms: partnerTermsQuery.data,

    isLoading,
    acceptedTerms,
    setAcceptedTerms,
    clearAcceptedTerms,
  };
};
